import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User as FirebaseUser } from 'firebase/auth'
import type { User, UserPreferences } from '../types/user'
import { defaultUserPreferences } from '../types/user'
import { authService } from '../services/authService'
import { userService } from '../services/userService'
import { auth } from '../utils/firebase'

type AppState = {
  isDarkMode: boolean
  userName: string
  user: User | null
  isInitialized: boolean
  isLoading: boolean
  error: Error | null
  toggleDarkMode: () => void
  setUserName: (name: string) => void
  updateUser: (updates: Partial<User>) => Promise<void>
  updatePreferences: (preferences: UserPreferences) => Promise<void>
  initializeUser: (firebaseUser: FirebaseUser) => Promise<void>
  setIsDarkMode: (isDark: boolean) => void
  fetchProfile: () => Promise<User | null>
  setIsInitialized: (isInitialized: boolean) => void
  setUser: (user: User | null) => void
  logout: () => Promise<void>
  clearUser: () => void
  setError: (error: Error | null) => void
}

export const useStore = create<AppState>()(persist((set, get) => ({
  user: null,
  isInitialized: false,
  isDarkMode: false,
  userName: '',
  isLoading: false,
  error: null,

  toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),
  setUserName: (userName) => set({ userName }),
  setIsDarkMode: (isDarkMode) => set({ isDarkMode }),
  setIsInitialized: (isInitialized) => set({ isInitialized }),
  setUser: (user) => set({ user }),
  setError: (error) => set({ error }),
  clearUser: () => set({ user: null, isInitialized: false, isLoading: false, error: null }),

  updateUser: async (updates) => {
    const current = get().user
    if (!current) throw new Error('No user is signed in.')
    set({ isLoading: true, error: null })
    try {
      await userService.updateUser(current.id, updates)
      set({ user: { ...current, ...updates }, isLoading: false })
    } catch (error) {
      set({ error: error instanceof Error ? error : new Error('Failed to update the profile.'), isLoading: false })
      throw error
    }
  },

  updatePreferences: async (preferences) => {
    const current = get().user
    if (!current) throw new Error('No user is signed in.')
    const merged = {
      ...defaultUserPreferences,
      ...current.preferences,
      ...preferences,
    }
    await userService.updateUserPreferences(current.id, merged)
    set({ user: { ...current, preferences: merged } })
  },

  initializeUser: async (firebaseUser) => {
    set({ isLoading: true, error: null })
    try {
      await authService.ensureUserDocument(firebaseUser)
      const user = await userService.getCurrentUser()
      if (!user) throw new Error('The user profile could not be loaded.')
      set({ user, isInitialized: true, isLoading: false })
    } catch (error) {
      set({ user: null, isInitialized: true, isLoading: false, error: error instanceof Error ? error : new Error('Failed to initialize the profile.') })
      throw error
    }
  },

  fetchProfile: async () => {
    set({ isLoading: true, error: null })
    try {
      const user = await userService.getCurrentUser()
      if (user) set({ user })
      set({ isLoading: false })
      return user
    } catch (error) {
      set({ isLoading: false, error: error instanceof Error ? error : new Error('Failed to load the profile.') })
      throw error
    }
  },

  logout: async () => {
    await auth.signOut()
    set({ user: null })
  },
}), {
  name: 'app-storage',
  partialize: (state) => ({ isDarkMode: state.isDarkMode, user: state.user }),
}))
